/**
 * Contact form used for both creating and editing a CRM contact.
 * - Partial analysis scores drive the overall score (same formula as the screening).
 * - Analysis can be imported from the screening JSON export.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    X, Save, Star, TrendingUp, Coins, Zap, Activity, AlertTriangle,
    Phone, Mail, AlertCircle, UserPlus, FileJson, Paperclip, Loader2,
} from "lucide-react";
import { AppButton } from "../ui/components";
import { Contact, ContactFields } from "../../types/objects";
import { computeFinalScore, fromCrmImport } from "../../utils/scoring";
import { mediaUrl, toUserMessage } from "../../api/strapi";

export const ANALYSIS_AREAS = [
    { id: "vision", label: "Vize a strategie", icon: <TrendingUp size={20} />, color: "accent-blue-600" },
    { id: "finance", label: "Finanční zdraví", icon: <Coins size={20} />, color: "accent-green-600" },
    { id: "implementation", label: "Realizace a IT", icon: <Zap size={20} />, color: "accent-purple-600" },
    { id: "risks", label: "Řízení rizik", icon: <AlertTriangle size={20} />, color: "accent-red-600" },
    { id: "operations", label: "Provozní efektivita", icon: <Activity size={20} />, color: "accent-orange-600" },
] as const;

type AreaId = typeof ANALYSIS_AREAS[number]["id"];
const scoreKey = (id: AreaId) => `analysis_${id}_score` as keyof ContactFields;
const textKey = (id: AreaId) => `analysis_${id}_text` as keyof ContactFields;

export const EMPTY_CONTACT: ContactFields = {
    fullname: "",
    phone: "",
    email: "",
    kind: "Lead",
    score_final: 0,
    analysis_vision_score: 0,
    analysis_vision_text: "",
    analysis_finance_score: 0,
    analysis_finance_text: "",
    analysis_implementation_score: 0,
    analysis_implementation_text: "",
    analysis_risks_score: 0,
    analysis_risks_text: "",
    analysis_operations_score: 0,
    analysis_operations_text: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Returns Czech validation messages; empty array means the form is valid. */
export function validateContact(data: ContactFields): string[] {
    const errors: string[] = [];
    if (!data.fullname.trim()) errors.push("Název subjektu je povinný.");
    if (!data.email.trim()) errors.push("E-mail je povinný.");
    else if (!EMAIL_RE.test(data.email.trim())) errors.push("E-mail nemá platný formát.");
    if (data.phone && !/^[+\d\s()-]{6,20}$/.test(data.phone.trim())) errors.push("Telefon smí obsahovat jen číslice, mezery a znaky + ( ) -.");
    return errors;
}

/** Builds the payload for Strapi: only schema fields, numbers as numbers, recalculated score. */
export function toContactPayload(data: ContactFields): ContactFields {
    const payload = { ...EMPTY_CONTACT };
    for (const key of Object.keys(EMPTY_CONTACT) as Array<keyof ContactFields>) {
        (payload as any)[key] = typeof EMPTY_CONTACT[key] === "number" ? Number(data[key]) || 0 : String(data[key] ?? "").trim();
    }
    payload.kind = data.kind;
    payload.score_final = computeFinalScore(ANALYSIS_AREAS.map((a) => payload[scoreKey(a.id)]));
    return payload;
}

interface CrmContactDetailProps {
    isOpen: boolean;
    onClose: () => void;
    /** Persists the form; `documentId` is set when editing an existing contact. */
    onSave: (data: ContactFields, documentId: string | null) => Promise<void>;
    /** Existing contact (edit mode) or prefilled fields (create mode, e.g. from screening). */
    initialData?: Partial<Contact> | null;
}

export default function CrmContactDetail({ isOpen, onClose, onSave, initialData }: CrmContactDetailProps) {
    const [formData, setFormData] = useState<ContactFields>(EMPTY_CONTACT);
    const [errors, setErrors] = useState<string[]>([]);
    const [apiError, setApiError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const jsonInputRef = useRef<HTMLInputElement>(null);

    const documentId = initialData?.documentId ?? null;
    const isEdit = !!documentId;

    useEffect(() => {
        if (!isOpen) return;
        const merged = { ...EMPTY_CONTACT };
        for (const key of Object.keys(EMPTY_CONTACT) as Array<keyof ContactFields>) {
            const value = initialData?.[key];
            if (value !== undefined && value !== null) (merged as any)[key] = value;
        }
        setFormData(merged);
        setErrors([]);
        setApiError(null);
    }, [initialData, isOpen]);

    const finalScore = useMemo(
        () => computeFinalScore(ANALYSIS_AREAS.map((a) => formData[scoreKey(a.id)])),
        [formData]
    );

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === "range" ? Number(value) : value }));
        if (errors.length) setErrors([]);
    };

    const handleJsonImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const fields = fromCrmImport(JSON.parse(String(reader.result)));
                setFormData((prev) => {
                    const next = { ...prev };
                    for (const [key, value] of Object.entries(fields)) {
                        // Keep existing identity values when the import does not provide them.
                        if (value === "" && typeof (prev as any)[key] === "string" && (prev as any)[key]) continue;
                        (next as any)[key] = value;
                    }
                    return next;
                });
                setErrors([]);
            } catch (err) {
                console.warn("[CrmContactDetail] Invalid JSON import", err);
                setErrors(["Soubor není platný JSON export ze screeningu."]);
            }
        };
        reader.readAsText(file);
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const validation = validateContact(formData);
        setErrors(validation);
        if (validation.length) return;
        setSaving(true);
        try {
            await onSave(toContactPayload(formData), documentId);
            onClose();
        } catch (err) {
            console.error("[CrmContactDetail] Save failed", err);
            setApiError(toUserMessage(err, "Záznam se nepodařilo uložit."));
        } finally {
            setSaving(false);
        }
    };

    const getScoreColor = (val: number) => (val >= 70 ? "text-green-500" : val >= 40 ? "text-orange-400" : "text-red-400");
    const canSubmit = !!formData.fullname.trim() && !!formData.email.trim() && !saving;
    const attachments = initialData?.attachments ?? [];

    return (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-2 md:p-4 bg-slate-900/75 backdrop-blur-md" role="dialog" aria-modal="true" aria-label={isEdit ? "Upravit subjekt" : "Nový subjekt"}>
            <div className="relative bg-white w-full max-w-3xl max-h-[98vh] md:max-h-[92vh] shadow-2xl flex flex-col rounded-[2.5rem] border border-white/20 overflow-hidden">

                <div className="p-6 md:p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                    <div className="flex items-center gap-5 text-left">
                        <div className="p-3 bg-blue-600 rounded-2xl text-white shadow-lg shadow-blue-200">
                            {isEdit ? <Star size={24} /> : <UserPlus size={24} />}
                        </div>
                        <div>
                            <h2 className="text-xl md:text-2xl font-black text-slate-900 leading-none">
                                {isEdit ? "Upravit subjekt" : "Nový subjekt"}
                            </h2>
                            <p className="text-[11px] font-bold text-blue-500 tracking-widest mt-1 uppercase">
                                {isEdit ? `ID záznamu: ${documentId}` : "Registrace do CRM"}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Zavřít" className="p-2 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all text-slate-300">
                        <X size={32} />
                    </button>
                </div>

                {errors.length > 0 && (
                    <div className="mx-6 mt-6 p-4 bg-red-50 border-2 border-red-100 rounded-3xl shrink-0 text-left" role="alert">
                        <div className="flex items-start gap-3 text-red-600 font-bold">
                            <AlertCircle size={20} className="shrink-0 mt-0.5" />
                            <div>
                                <p className="text-xs tracking-widest mb-1 text-red-700 uppercase">Zkontrolujte formulář:</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    {errors.map((err) => <li key={err} className="text-sm">{err}</li>)}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="flex-1 overflow-y-auto p-6 md:p-10 space-y-12 custom-scrollbar">
                    <div className="flex justify-center">
                        <input type="file" accept=".json,application/json" ref={jsonInputRef} onChange={handleJsonImport} className="hidden" data-testid="json-input" />
                        <AppButton
                            variant="outline"
                            onClick={() => jsonInputRef.current?.click()}
                            className="w-full border-dashed border-2 py-5 rounded-3xl text-blue-600 border-blue-200 hover:bg-blue-50 font-black text-xs uppercase"
                        >
                            <FileJson size={20} className="mr-3" /> Načíst analýzu ze souboru (JSON ze screeningu)
                        </AppButton>
                    </div>

                    <section className="space-y-8">
                        <h3 className="text-sm font-black text-slate-900 tracking-[0.2em] flex items-center gap-3 border-l-4 border-blue-600 pl-4 text-left uppercase">
                            01. Identifikace
                        </h3>
                        <div className="flex flex-col gap-6 text-left">
                            <label className="flex flex-col gap-2">
                                <span className={`text-[11px] font-black tracking-widest ml-1 uppercase ${!formData.fullname ? "text-red-400" : "text-slate-400"}`}>Celé jméno / název firmy *</span>
                                <input name="fullname" value={formData.fullname} onChange={handleChange} placeholder="Zadejte název…" className="w-full p-5 rounded-2xl border-2 border-slate-100 focus:border-blue-600 outline-none font-bold text-lg bg-slate-50/30" />
                            </label>
                            <label className="flex flex-col gap-2">
                                <span className={`text-[11px] font-black tracking-widest ml-1 flex items-center gap-2 uppercase ${!formData.email ? "text-red-400" : "text-slate-400"}`}><Mail size={12} /> E-mailová adresa *</span>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@domena.cz" className="w-full p-5 rounded-2xl border-2 border-slate-100 focus:border-blue-600 outline-none font-bold bg-slate-50/30" />
                            </label>
                            <label className="flex flex-col gap-2">
                                <span className="text-[11px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2 uppercase"><Phone size={12} /> Telefonní číslo</span>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+420 …" className="w-full p-5 rounded-2xl border-2 border-slate-100 focus:border-blue-600 outline-none font-bold bg-slate-50/30" />
                            </label>
                            <label className="flex flex-col gap-2">
                                <span className="text-[11px] font-black text-slate-400 tracking-widest ml-1 uppercase">Kategorie</span>
                                <select name="kind" value={formData.kind} onChange={handleChange} className="w-full p-5 rounded-2xl border-2 border-slate-100 font-bold bg-white cursor-pointer">
                                    <option value="Lead">Lead (zájemce)</option>
                                    <option value="Osoba">Fyzická osoba</option>
                                    <option value="Společnost">Právnická osoba</option>
                                </select>
                            </label>
                        </div>
                    </section>

                    {attachments.length > 0 && (
                        <section className="space-y-3 text-left">
                            <h3 className="text-sm font-black text-slate-900 tracking-[0.2em] flex items-center gap-3 border-l-4 border-blue-600 pl-4 uppercase">Přílohy</h3>
                            <ul className="space-y-2">
                                {attachments.map((file) => (
                                    <li key={file.id}>
                                        <a href={mediaUrl(file) ?? "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 font-bold hover:underline">
                                            <Paperclip size={14} /> {file.name ?? "soubor"}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section className="pt-10 border-t border-slate-100">
                        <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
                            <div className="space-y-1 text-center md:text-left">
                                <h3 className="text-base font-black tracking-[0.2em] flex items-center justify-center md:justify-start gap-3 uppercase">
                                    <Zap size={20} className="text-orange-500" strokeWidth={3} /> 02. Hloubkový scoring
                                </h3>
                                <p className="text-[11px] text-slate-400 font-bold tracking-widest uppercase">Celkové skóre = průměr pěti oblastí</p>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className={`text-6xl font-black tracking-tighter ${getScoreColor(finalScore)}`} data-testid="final-score">{finalScore}%</span>
                            </div>
                        </div>
                    </section>

                    <section className="space-y-10 pb-10">
                        {ANALYSIS_AREAS.map((item) => (
                            <div key={item.id} className="p-8 bg-slate-50 rounded-[3rem] border border-slate-100 space-y-8 shadow-sm text-left">
                                <div className="flex justify-between items-center px-1">
                                    <label htmlFor={`score-${item.id}`} className="text-xs font-black text-slate-600 tracking-[0.15em] flex items-center gap-3 uppercase">
                                        {item.icon} {item.label}
                                    </label>
                                    <div className="bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm">
                                        <span className="text-2xl font-black text-slate-900">{formData[scoreKey(item.id)]}%</span>
                                    </div>
                                </div>
                                <input
                                    id={`score-${item.id}`}
                                    type="range"
                                    min={0}
                                    max={100}
                                    name={scoreKey(item.id)}
                                    value={Number(formData[scoreKey(item.id)]) || 0}
                                    onChange={handleChange}
                                    className={`w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer ${item.color}`}
                                />
                                <label className="block space-y-3">
                                    <span className="text-[11px] font-black text-slate-400 tracking-[0.2em] ml-2 uppercase">Podrobná analýza oblasti</span>
                                    <textarea
                                        name={textKey(item.id)}
                                        value={String(formData[textKey(item.id)] ?? "")}
                                        onChange={handleChange}
                                        placeholder="Zadejte postřehy…"
                                        className="w-full p-6 rounded-3xl border-2 border-slate-200 outline-none focus:border-blue-600 focus:bg-white min-h-[160px] font-medium text-slate-700 transition-all leading-relaxed"
                                    />
                                </label>
                            </div>
                        ))}
                    </section>
                </form>

                <div className="p-6 md:p-10 border-t border-slate-100 bg-white flex flex-col md:flex-row gap-5 shrink-0">
                    <AppButton
                        onClick={() => handleSubmit()}
                        variant={canSubmit ? "darkBlue" : "outline"}
                        className="flex-1 py-6 text-xl font-black uppercase tracking-[0.1em] shadow-2xl"
                        disabled={saving}
                    >
                        {saving ? <Loader2 size={24} className="animate-spin" /> : <Save size={24} className="mr-2" />}
                        {isEdit ? "Uložit změny" : "Založit subjekt"}
                    </AppButton>
                    <button type="button" onClick={onClose} className="px-12 py-6 font-black border-2 border-slate-200 rounded-[2rem] text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all uppercase text-sm tracking-widest">
                        Zrušit
                    </button>
                </div>

                {apiError && (
                    <div className="absolute inset-0 z-[4000] bg-white/95 backdrop-blur-xl flex items-center justify-center p-6" role="alert">
                        <div className="max-w-sm w-full text-center space-y-6">
                            <div className="w-24 h-24 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-red-100">
                                <AlertCircle size={48} />
                            </div>
                            <div className="space-y-2">
                                <h4 className="text-2xl font-black text-slate-900">Uložení se nezdařilo</h4>
                                <p className="text-slate-500 font-bold leading-relaxed px-4">{apiError}</p>
                            </div>
                            <button type="button" onClick={() => setApiError(null)} className="w-[80%] py-4 bg-slate-900 text-white rounded-2xl font-black uppercase hover:bg-slate-800 transition-all shadow-lg mx-auto">
                                Rozumím
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
